/**
 * Sitemap page — page-sitemap.js
 *
 * Responsibilities:
 *  1. Fetch Dogs & Cats collection filters via Section Rendering API
 *  2. Render filter links into the placeholder columns
 *  3. Client-side search: debounced filtering of all rendered links
 *
 * Data flow:
 *  - Static sections (About Nulo, Blog) are server-rendered in Liquid
 *  - Dynamic sections (Dogs, Cats) are populated by this JS after load
 */

const DEBOUNCE_MS = 300;

class SitemapMain extends HTMLElement {
  /** @type {AbortController|null} */
  #abort = null;

  /** @type {ReturnType<typeof setTimeout>|null} */
  #debounceTimer = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Search
    const input = this.querySelector('[data-sitemap-search]');
    const clearBtn = this.querySelector('[data-sitemap-clear]');

    if (input) {
      input.addEventListener('input', () => {
        this.#syncClearButton(input.value);
        this.#scheduleSearch(input.value);
      }, { signal });

      this.#syncClearButton(input.value);
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        if (!input) return;
        input.value = '';
        this.#syncClearButton('');
        clearTimeout(this.#debounceTimer);
        this.#applySearch('');
        input.focus();
      }, { signal });
    }

    // Load dynamic filter sections (dogs, cats)
    this.#loadDynamicSections();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#debounceTimer);
  }

  // ---------------------------------------------------------------------------
  // Dynamic filter fetching via Section Rendering API
  // ---------------------------------------------------------------------------

  async #loadDynamicSections() {
    const targets = this.querySelectorAll('[data-sitemap-collection]');
    if (!targets.length) return;

    // Prefer setting; fall back to collection template helper section ID.
    const rawId = this.dataset.filterSectionId?.trim() || 'sitemap_filters_helper';
    const helperSectionId = rawId.replace(/^shopify-section-/, '');

    await Promise.all(
      [...targets].map((target) => this.#loadCollection(target, helperSectionId))
    );

    // After all dynamic content is loaded, re-run any active search
    const input = this.querySelector('[data-sitemap-search]');
    if (input?.value?.trim()) {
      this.#applySearch(input.value.trim().toLowerCase());
    }
  }

  async #loadCollection(target, helperSectionId) {
    const handle = target.dataset.sitemapCollection;
    const loader = target.querySelector('[data-sitemap-loading]');

    try {
      const url = `${this.#localeRoot()}collections/${handle}?section_id=${encodeURIComponent(helperSectionId)}`;
      const res = await fetch(url, {
        headers: { Accept: 'text/html' },
        signal: this.#abort?.signal,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const jsonEl = doc.querySelector('[data-sitemap-filters]');

      if (!jsonEl) throw new Error('No filter data found');

      const data = JSON.parse(jsonEl.textContent);
      this.#renderFilters(target, data);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.warn(`[sitemap] Failed to load filters for "${handle}":`, err);
      if (loader) loader.hidden = true;
    }
  }

  /**
   * Locale subfolder prefix for Markets (e.g. "/en-gb/"). Always ends with "/".
   * @returns {string}
   */
  #localeRoot() {
    const root = window.Shopify?.routes?.root;
    if (typeof root === 'string' && root.length > 0) {
      return root.endsWith('/') ? root : `${root}/`;
    }
    return '/';
  }

  /**
   * Placeholder metafield values that should never appear as sitemap links.
   * Matches collection-main.js `isHiddenFilterOption`.
   * @param {string} [label]
   * @param {string} [value]
   * @returns {boolean}
   */
  #isHiddenFilterOption(label, value) {
    const normalize = (input) => String(input || '').trim().toLowerCase();
    const normalizedLabel = normalize(label);
    const normalizedValue = normalize(value);

    return (
      normalizedLabel === 'n/a' ||
      normalizedLabel === 'na' ||
      normalizedValue === 'n/a' ||
      normalizedValue === 'na'
    );
  }

  /**
   * Builds columns from filter groups and inserts them into the target element.
   * Each filter group (e.g. "Product Type", "Life Stage") becomes one column.
   * The column title is the filter label; each value becomes a link.
   *
   * @param {HTMLElement} target - The placeholder element with data-sitemap-collection
   * @param {Object} data - Parsed filter JSON { handle, url, filters: [] }
   */
  #renderFilters(target, data) {
    const loader = target.querySelector('[data-sitemap-loading]');
    const grid = target.querySelector('[data-sitemap-columns]');
    if (!grid) return;

    // Hide loading indicator; clear prior render (Theme Editor reconnect / re-fetch)
    if (loader) loader.hidden = true;
    grid.replaceChildren();

    for (const filterGroup of data.filters || []) {
      const seen = new Set();
      const values = [];

      for (const v of filterGroup.values || []) {
        if (!(v.count > 0) || !v.url_to_add) continue;
        if (this.#isHiddenFilterOption(v.label, v.value)) continue;

        const key = String(v.value || v.label || '').trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        values.push(v);
      }

      if (!values.length) continue;

      const col = document.createElement('div');
      col.className = 'sitemap-column';
      col.setAttribute('data-sitemap-col', '');

      const title = document.createElement('p');
      title.className = 'sitemap-column__title';
      title.textContent = filterGroup.label;
      col.appendChild(title);

      for (const val of values) {
        const a = document.createElement('a');
        a.className = 'sitemap-link';
        a.setAttribute('data-sitemap-link', '');
        // url_to_add is a full URL like /collections/for-dogs?filter.p.m.animal=Dog
        try {
          const parsed = new URL(val.url_to_add, window.location.origin);
          a.href = parsed.pathname + parsed.search;
        } catch {
          a.href = val.url_to_add;
        }
        a.textContent = val.label;
        col.appendChild(a);
      }

      grid.appendChild(col);
    }

    // Show the section now that it has content
    const section = target.closest('[data-sitemap-section]');
    if (section && grid.querySelector('.sitemap-column')) {
      section.hidden = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Client-side search
  // ---------------------------------------------------------------------------

  /**
   * Swap the decorative search icon for a clear button when the field has text.
   * @param {string} value
   */
  #syncClearButton(value) {
    const hasValue = Boolean(value?.trim());
    const searchIcon = this.querySelector('[data-sitemap-search-icon]');
    const clearBtn = this.querySelector('[data-sitemap-clear]');

    if (searchIcon) searchIcon.hidden = hasValue;
    if (clearBtn) {
      clearBtn.hidden = !hasValue;
      clearBtn.setAttribute('aria-hidden', hasValue ? 'false' : 'true');
    }
  }

  #scheduleSearch(value) {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#applySearch(value.trim().toLowerCase());
    }, DEBOUNCE_MS);
  }

  #applySearch(query) {
    const sections = this.querySelectorAll('[data-sitemap-section]');
    let totalVisible = 0;

    for (const section of sections) {
      let sectionVisible = 0;
      const cols = section.querySelectorAll('[data-sitemap-col]');

      for (const col of cols) {
        let colVisible = 0;
        const links = col.querySelectorAll('[data-sitemap-link]');

        for (const link of links) {
          const matches = !query || link.textContent.trim().toLowerCase().includes(query);
          link.hidden = !matches;
          if (matches) colVisible++;
        }

        col.hidden = colVisible === 0;
        sectionVisible += colVisible;
      }

      section.hidden = sectionVisible === 0;
      totalVisible += sectionVisible;
    }

    const noResults = this.querySelector('[data-sitemap-no-results]');
    if (noResults) noResults.hidden = totalVisible > 0;
  }
}

if (!customElements.get('sitemap-main')) {
  customElements.define('sitemap-main', SitemapMain);
}
